import type { OuraDailyEntry, OuraVo2Max } from "../data/ouraTypes";

import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";

interface VascularAgeData {
  timestamp: string | undefined;
  vo2_max: number | undefined;
}

interface CardiovascularAgeLike {
  day?: string;
  vascular_age?: number;
}

interface Vo2Like {
  timestamp?: string;
  vo2_max?: number;
}

interface OuraEntryLike {
  cardiovascular_age?: CardiovascularAgeLike | null;
  vo2?: null | Vo2Like;
}

const isOuraEntryLike = (value: unknown): value is OuraEntryLike =>
  typeof value === "object" && value !== null;

/**
 * WHAT:
 *   Extracts vascular age data from cardiovascular_age object.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting data processing logic.
 *
 * @param cardiovascularAge - Cardiovascular age data object
 *
 * @returns VascularAgeData if valid data found, null otherwise
 *
 */
function extractCardiovascularAgeData(
  cardiovascularAge: CardiovascularAgeLike,
): null | VascularAgeData {
  if (
    typeof cardiovascularAge.vascular_age === "number" ||
    typeof cardiovascularAge.day === "string"
  ) {
    const result: VascularAgeData = {
      timestamp: typeof cardiovascularAge.day === "string" ? cardiovascularAge.day : undefined,
      vo2_max:
        typeof cardiovascularAge.vascular_age === "number"
          ? cardiovascularAge.vascular_age
          : undefined,
    };
    return result;
  }
  return null;
}

/**
 * WHAT:
 *   Extracts vascular age data from vo2 object.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting data processing logic.
 *
 * @param vo2 - Vo2 data object
 *
 * @returns VascularAgeData if valid data found, null otherwise
 *
 */
function extractVo2Data(vo2: Vo2Like): null | VascularAgeData {
  if (typeof vo2.vo2_max === "number") {
    const result: VascularAgeData = {
      timestamp: typeof vo2.timestamp === "string" ? vo2.timestamp : undefined,
      vo2_max: vo2.vo2_max,
    };
    return result;
  }
  return null;
}

/**
 * WHAT:
 *   Extracts vascular age data from vo2 entry.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting vo2 processing logic.
 *
 * @param entry - Oura entry with vo2 data
 *
 * @returns VascularAgeData if valid, null otherwise
 *
 */
function extractVo2FromHistory(
  entry: OuraDailyEntry & { vo2: OuraVo2Max },
): null | VascularAgeData {
  const vo2 = entry.vo2;
  if (
    typeof vo2 === "object" &&
    "timestamp" in vo2 &&
    "vo2_max" in vo2 &&
    typeof vo2.timestamp === "string" &&
    typeof vo2.vo2_max === "number"
  ) {
    return {
      timestamp: vo2.timestamp,
      vo2_max: vo2.vo2_max,
    };
  }
  return null;
}

/**
 * WHAT:
 *   Fetches and processes history data to find vascular age information.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting history data processing logic.
 *
 * @param historyData - Array of Oura daily entries
 *
 * @returns VascularAgeData if found in history, null otherwise
 *
 */
function fetchHistoryVascularData(historyData: OuraDailyEntry[]): null | VascularAgeData {
  // Note: cardiovascular_age is not part of OuraDailyEntry, so we only look for vo2 data

  const recentVascularData = historyData.find(
    (entry): entry is OuraDailyEntry & { vo2: OuraVo2Max } => entry.vo2?.vo2_max !== undefined,
  );

  if (recentVascularData) {
    return extractVo2FromHistory(recentVascularData);
  }

  return null;
}

/**
 * WHAT:
 *   Optimized Vascular Hook that fetches vascular age data.
 *
 * WHY:
 *   Fetches vascular age data from Oura data context or falls back to direct API call.
 *
 * @returns Object containing vascular age data, error, loading state, timestamp, and vo2Max
 *
 */
export const useOptimizedVascular = () => {
  const [data, setData] = useState<null | VascularAgeData>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchVascularAge = async () => {
      try {
        setLoading(true);
        setError(null);

        const ouraDataRaw: unknown = await core.invoke<null | OuraDailyEntry>(
          "get_latest_oura_data",
        );

        // Try cardiovascular_age first
        if (isOuraEntryLike(ouraDataRaw) && ouraDataRaw.cardiovascular_age) {
          const extracted = extractCardiovascularAgeData(ouraDataRaw.cardiovascular_age);
          if (extracted) {
            setData(extracted);
            return;
          }
        }

        // Try vo2 data
        if (isOuraEntryLike(ouraDataRaw) && ouraDataRaw.vo2) {
          const extracted = extractVo2Data(ouraDataRaw.vo2);
          if (extracted) {
            setData(extracted);
            return;
          }
        }

        // Fallback to history data
        try {
          const historyDataRaw: unknown = await core.invoke<null | OuraDailyEntry[]>(
            "sync_oura_history",
          );
          const historyData = Array.isArray(historyDataRaw)
            ? (historyDataRaw as OuraDailyEntry[])
            : null;

          if (historyData) {
            const historyResult = fetchHistoryVascularData(historyData);
            if (historyResult) {
              setData(historyResult);
              return;
            }
          }

          setData(null);
        } catch {
          // NOTE: History fetch failed - silently ignore
          setData(null);
        }
      } catch (error_) {
        // [useOptimizedVascular] Error
        setError((error_ as Error).message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchVascularAge();
  }, []);

  return {
    error,
    loading,
    timestamp: data?.timestamp ?? null,
    vascularAge: data,
    vo2Max: data?.vo2_max ?? 0,
  };
};
